import React, { useState } from "react"
import { useFormContext } from "react-hook-form"
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Typography
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useRemovePartner } from "../hooks/useRemovePartner"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { InviteStatusResult, PartnerInviteStatus } from "#/components/partner-access/partnerAccessTypes"

export function RemovePartnerButton({
  disabled,
  onPartnerRemoved,
  inviteData,
  isPartnerOnIdCard
}: {
  disabled?: boolean
  onPartnerRemoved: () => void
  inviteData: InviteStatusResult
  isPartnerOnIdCard: boolean
}): JSX.Element {
  const removePartnerMutation = useRemovePartner()
  const { reset, trigger } = useFormContext()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const theme = useTheme()
  const { isUsa } = useCurrentUser()

  const handleRemove = async () => {
    removePartnerMutation.mutate(undefined, {
      onSuccess: () => {
        setIsDialogOpen(false)

        // Reset partner fields to empty values
        reset(
          {
            partnerFirstName: "",
            partnerLastName: "",
            partnerPreferredName: "",
            partnerDateOfBirth: null,
            partnerSex: "",
            partnerGenderIdentity: "",
            partnerGenderIdentityOther: "",
            partnerEmail: ""
          },
          {
            keepValues: false,
            keepDirty: false,
            keepErrors: false,
            keepTouched: false,
            keepIsSubmitted: false,
            keepIsValid: false
          }
        )

        setTimeout(() => {
          trigger([
            "partnerFirstName",
            "partnerLastName",
            "partnerPreferredName",
            "partnerDateOfBirth",
            "partnerSex",
            "partnerGenderIdentity",
            "partnerGenderIdentityOther",
            "partnerEmail"
          ])
        }, 100)
        if (isUsa && onPartnerRemoved) {
          onPartnerRemoved()
        }
      }
    })
  }

  const intl = useIntl()
  const partnerAccessActiveOrInvited =
    inviteData?.status === PartnerInviteStatus.INVITED || inviteData?.status === PartnerInviteStatus.ACTIVE

  const dialogText = (() => {
    if (isPartnerOnIdCard && partnerAccessActiveOrInvited) {
      return intl.formatMessage({
        defaultMessage:
          "Your partner will also be removed from your member ID card, and they will no longer be able to access your account."
      })
    } else if (isPartnerOnIdCard && !partnerAccessActiveOrInvited) {
      return intl.formatMessage({ defaultMessage: "Your partner will also be removed from your member ID card." })
    } else if (!isPartnerOnIdCard && partnerAccessActiveOrInvited) {
      return intl.formatMessage({ defaultMessage: "This will also remove your partner's access to your account." })
    }
    return intl.formatMessage({ defaultMessage: "This will delete all partner information in this section." })
  })()

  const handleRemoveClick = () => {
    setIsDialogOpen(true)
  }

  return (
    <>
      <Box marginTop={theme.spacing(theme.tokens.spacing.xxl)}>
        <Button variant="outlined" color="secondary" onClick={handleRemoveClick} disabled={disabled}>
          <FormattedMessage defaultMessage="Remove partner"></FormattedMessage>
        </Button>
      </Box>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>
          <FormattedMessage defaultMessage="Remove partner?" />
        </DialogTitle>
        <DialogContent>
          <Typography>{dialogText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} variant="outlined" color="secondary">
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleRemove} variant="contained" color="error" disabled={removePartnerMutation.isLoading}>
            <FormattedMessage defaultMessage="Remove partner" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
